/**
 * Toast viewport (#679): bottom-right on desktop, full-width bottom on phones. Renders the app-wide `toasts`
 * store; each toast auto-dismisses after its ttl and has a close button.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useT } from '../i18n/useT';
import { toasts as defaultStore, type Toast, type ToastStore, type ToastTone } from './toastStore';

const TONE_CLS: Record<ToastTone, string> = {
  success: 'border-s-yes',
  info: 'border-s-accent',
  warn: 'border-s-warn',
  error: 'border-s-no',
};

function ToastItem({ t, store }: { t: Toast; store: ToastStore }) {
  const tr = useT();
  useEffect(() => {
    if (t.ttlMs <= 0) return;
    const h = setTimeout(() => store.dismiss(t.id), t.ttlMs);
    return () => clearTimeout(h);
  }, [t.id, t.ttlMs, store]);
  return (
    <li
      role={t.tone === 'error' ? 'alert' : 'status'}
      data-testid="toast"
      data-tone={t.tone}
      className={`card pointer-events-auto flex items-start gap-2 border-s-4 px-3 py-2 text-sm shadow-lg ${TONE_CLS[t.tone]}`}
    >
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{t.title}</div>
        {t.body && <div className="break-words text-xs text-muted">{t.body}</div>}
      </div>
      <button
        type="button"
        aria-label={tr('shell.toasts.dismiss')}
        onClick={() => store.dismiss(t.id)}
        className="rounded px-1 text-muted hover:text-fg"
      >
        ✕
      </button>
    </li>
  );
}

export default function Toaster({ store = defaultStore }: { store?: ToastStore }) {
  const items = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const t = useT();
  return (
    <ol
      aria-label={t('shell.toasts.label')}
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col gap-2 p-4 sm:start-auto sm:w-96"
    >
      {items.map((t) => (
        <ToastItem key={t.id} t={t} store={store} />
      ))}
    </ol>
  );
}
